/* Sondos shoppable-video shelf — sections/sondos-video-shelf.liquid
   ────────────────────────────────────────────────────────────────────────
   A <video> exists for each card that is fully on screen — two at 390px —
   and each one is destroyed the moment its card leaves, so the number of live
   videos never exceeds what is visible. See "Which cards play" below: two
   earlier attempts to pick a single winning card both failed, and the reason
   they failed is written there so nobody tries a third. Nothing here times or
   seeks: the uploaded -v2 assets are already cut to their window, so native
   `loop` is the whole playback model.

   Loading is never triggered from a first-interaction event — the observer
   does the work, and the only pointer listener is the pause button the user
   deliberately pressed. See the INP section of
   .cursor/rules/sondos-theme-performance-standards.mdc. */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-svs]');
  if (!roots.length || !('IntersectionObserver' in window)) return;

  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* Data Saver, 2G and reduced motion all land in the same place: the poster
     stays and the card plays only when its button is pressed. */
  var holdBack = reduce || !!conn.saveData || /^(slow-)?2g$/.test(conn.effectiveType || '');

  Array.prototype.forEach.call(roots, function (root) {
    init(root);
  });

  function init(root) {
    var rail = root.querySelector('[data-svs-rail]');
    var cards = root.querySelectorAll('[data-svs-card]');
    if (!rail || !cards.length) return;

    var active = [];
    var paused = holdBack;
    var dialog = root.querySelector('[data-svs-dialog]');
    var viewerOpen = false;
    var opener = null;
    var oldHtmlOverflow = '';
    var oldBodyOverflow = '';

    if (dialog && typeof dialog.showModal === 'function') {
      root.querySelector('[data-svs-close]').addEventListener('click', function () { dialog.close(); });
      dialog.addEventListener('close', function () {
        var video = dialog.querySelector('video');
        if (video) {
          video.pause();
          video.removeAttribute('src');
          video.load();
          video.remove();
        }
        document.documentElement.style.overflow = oldHtmlOverflow;
        document.body.style.overflow = oldBodyOverflow;
        viewerOpen = false;
        if (opener && opener.isConnected) opener.focus({ preventScroll: true });
        update();
      });
      root.querySelectorAll('[data-svs-expand]').forEach(function (button) {
        button.addEventListener('click', function () {
          if (viewerOpen) return;
          var card = button.closest('[data-svs-card]');
          var inlineVideo = card.querySelector('video');
          var startAt = inlineVideo ? inlineVideo.currentTime : 0;
          var poster = card.querySelector('.svs__poster');
          var title = card.querySelector('.svs__title');
          var ratio = poster && poster.naturalHeight ? poster.naturalWidth / poster.naturalHeight : 9 / 16;
          dialog.style.setProperty('--svs-video-ratio', ratio);
          viewerOpen = true;
          opener = button;
          clearTimeout(settle);
          active.slice().forEach(teardown);
          oldHtmlOverflow = document.documentElement.style.overflow;
          oldBodyOverflow = document.body.style.overflow;
          document.documentElement.style.overflow = 'hidden';
          document.body.style.overflow = 'hidden';
          var video = document.createElement('video');
          video.controls = true;
          video.playsInline = true;
          video.muted = true;
          video.loop = true;
          video.setAttribute('playsinline', '');
          video.setAttribute('aria-label', title ? title.textContent.trim() : 'فيديو المنتج');
          if (poster) video.poster = poster.currentSrc || poster.src;
          video.addEventListener('loadedmetadata', function () {
            if (video.videoWidth && video.videoHeight) dialog.style.setProperty('--svs-video-ratio', video.videoWidth / video.videoHeight);
            if (startAt > 0 && startAt < video.duration) video.currentTime = startAt;
          }, { once: true });
          video.src = card.getAttribute('data-svs-src');
          dialog.querySelector('[data-svs-player]').appendChild(video);
          dialog.showModal();
          var playing = video.play();
          if (playing && playing.catch) playing.catch(function () { /* Native controls remain available. */ });
        });
      });
    } else {
      root.querySelectorAll('[data-svs-expand]').forEach(function (button) { button.hidden = true; });
    }

    /* The controls are not chrome on ten cards: by default only the card that
       is actually moving carries its stop button. They become permanent the
       moment the shelf is hand-driven — because autoplay is suppressed, or
       because the visitor pressed pause and now needs a way back. */
    if (holdBack) root.classList.add('svs--manual');

    function teardown(card) {
      if (!card) return;
      var v = card.querySelector('video');
      if (v) {
        v.pause();
        /* Drop the source and re-load before removing: detaching a <video>
           that still has a src leaves the fetch running in Safari. */
        v.removeAttribute('src');
        v.load();
        v.remove();
      }
      card.classList.remove('is-playing');
      setLabel(card, false);
      var i = active.indexOf(card);
      if (i > -1) active.splice(i, 1);
    }

    function play(card) {
      if (!card || active.indexOf(card) > -1) return;
      var media = card.querySelector('[data-svs-media]');
      var src = card.getAttribute('data-svs-src');
      if (!media || !src) return;

      active.push(card);

      var v = document.createElement('video');
      v.className = 'svs__video';
      /* Both the property and the attribute: iOS honours the attribute at
         parse time, and the property is what the play() promise checks. */
      v.muted = true;
      v.defaultMuted = true;
      v.setAttribute('muted', '');
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
      v.loop = true;
      v.setAttribute('loop', '');
      v.preload = 'auto';
      v.disableRemotePlayback = true;
      /* Decorative: the product name is already the card's accessible name. */
      v.setAttribute('aria-hidden', 'true');
      v.tabIndex = -1;

      v.addEventListener('playing', function () {
        card.classList.add('is-playing');
        setLabel(card, true);
      });
      /* A failed fetch or an unsupported codec leaves the poster in place. */
      v.addEventListener('error', function () {
        teardown(card);
      });

      v.src = src;
      media.appendChild(v);

      var p = v.play();
      if (p && p.catch) {
        p.catch(function () {
          /* Autoplay refused (some in-app webviews). Poster stays; the button
             still works, because that press is a user gesture. */
          teardown(card);
        });
      }
    }

    function setLabel(card, playing) {
      var btn = card.querySelector('[data-svs-toggle]');
      if (btn) btn.setAttribute('aria-label', playing ? 'إيقاف الفيديو' : 'تشغيل الفيديو');
    }

    /* ── Which cards play ───────────────────────────────────────────────────
       Every card that is fully on screen plays. There is deliberately no
       "chosen" card any more, because two attempts at choosing one both
       failed on the same underlying fact: at 390px TWO cards are fully
       visible, and neither of them is "the one you are on".

         Attempt 1 — highest `intersectionRatio`. Both cards report exactly 1,
         so the tie fell to whichever the observer recorded first: always the
         DOM-first card, i.e. the right-hand one in RTL. It also cannot see a
         horizontal swipe at all — a card sliding across the screen while
         staying fully visible crosses no threshold, so nothing fires.

         Attempt 2 — nearest the centre of the viewport. Measured on R3 at
         rest: the rail snaps card #1 to the start edge, while the centre rule
         picks card #2. It played the card AFTER the one the rail had just
         landed on, every single time. Worse than attempt 1, not better.

       Both failures were really the same bug: inventing a winner where the
       layout does not have one. So the rule is now visibility, which is a
       fact rather than a guess — whole card on screen, it plays; a peeking
       sliver never does; a card half off the bottom never does. */
    function visible(card) {
      var r = card.getBoundingClientRect();
      var vw = document.documentElement.clientWidth;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var visW = Math.min(r.right, vw) - Math.max(r.left, 0);
      var visH = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      if (visW <= 0 || visH <= 0) return false;
      return visW / r.width >= 0.9 && visH / r.height >= 0.55;
    }

    function update() {
      clearTimeout(settle);
      if (viewerOpen) return;

      /* Release anything that has left, immediately and in every mode — this
         is what keeps the number of live videos equal to what is on screen
         instead of growing as the rail is swiped. */
      active.slice().forEach(function (card) {
        if (!visible(card)) teardown(card);
      });
      if (paused) return;

      var want = [];
      Array.prototype.forEach.call(cards, function (card) {
        if (visible(card) && active.indexOf(card) === -1) want.push(card);
      });
      if (want.length) commit(want);
    }

    /* Start only once the rail has stopped. A flung rail would otherwise pay
       for every card it passes; ten cards is ~8 MB of video nobody watched.
       Selection stays live, the downloads do not. */
    var settle = null;
    function commit(list) {
      clearTimeout(settle);
      settle = setTimeout(function () {
        list.forEach(function (card) { if (visible(card)) play(card); });
      }, 280);
    }

    var near = false;
    var queued = false;
    function schedule() {
      if (!near || queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; update(); });
    }

    /* The observer's only job now is to say whether the shelf is anywhere near
       the viewport, so the scroll handler does no rect math on a page that is
       nowhere near it. */
    var io = new IntersectionObserver(function (entries) {
      near = entries[0].isIntersecting;
      update();
    }, { rootMargin: '200px 0px' });
    io.observe(root);

    window.addEventListener('scroll', schedule, { passive: true });
    rail.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    Array.prototype.forEach.call(cards, function (card) {
      var btn = card.querySelector('[data-svs-toggle]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        root.classList.add('svs--manual');
        /* A press beats anything the scroll handler had queued. */
        clearTimeout(settle);
        if (active.indexOf(card) > -1) {
          /* An explicit stop stops the whole shelf, not just this card —
             otherwise the neighbour keeps moving and the next scroll starts
             more, and the stop meant nothing. WCAG 2.2.2 asks for a stop to
             the motion, not for one of several. */
          paused = true;
          active.slice().forEach(teardown);
        } else {
          /* Under reduced motion / Data Saver / 2G the shelf stays hand-driven
             for the rest of the visit: this press plays this card and nothing
             else starts on scroll. */
          paused = holdBack;
          play(card);
        }
      });
    });

    /* ── Desktop arrows ─────────────────────────────────────────────────── */
    var prev = root.querySelector('[data-svs-prev]');
    var next = root.querySelector('[data-svs-next]');
    if (!prev || !next) return;

    var rtl = getComputedStyle(rail).direction === 'rtl';
    var position = root.querySelector('[data-svs-position]');

    function step() {
      var item = rail.querySelector('.svs__item');
      var gap = parseFloat(getComputedStyle(rail).columnGap) || 16;
      return (item ? item.getBoundingClientRect().width : 214) + gap;
    }

    function move(forward) {
      /* scrollBy takes a physical axis. In RTL, moving further along the list
         means moving the content to the right, i.e. a negative delta — every
         current engine reports RTL scrollLeft as 0 at the start running
         negative toward the end. */
      var delta = step() * (forward ? 1 : -1) * (rtl ? -1 : 1);
      rail.scrollBy({ left: delta, behavior: reduce ? 'auto' : 'smooth' });
    }

    function sync() {
      var max = rail.scrollWidth - rail.clientWidth;
      /* Math.abs covers both scrollLeft conventions without branching. */
      var x = Math.abs(rail.scrollLeft);
      prev.disabled = x <= 1;
      next.disabled = x >= max - 1;
      if (position) {
        position.classList.toggle('is-scrollable', max > 1);
        position.style.setProperty('--svs-progress', max > 1 ? Math.min(1, Math.max(0, x / max)) : 0);
      }
    }

    prev.addEventListener('click', function () { move(false); });
    next.addEventListener('click', function () { move(true); });
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });

    /* sync() must not be trusted at parse time. This section's stylesheet is
       deferred (media="print" onload), so when the deferred script first runs
       the rail is still unstyled: scrollWidth === clientWidth, max === 0, and
       BOTH arrows latch disabled with nothing left to re-enable them. That is
       exactly what shipped and measured as "no arrows on desktop".
       A ResizeObserver on the rail catches the stylesheet landing, the font
       swap, and any later reflow. */
    sync();
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(rail);
    else window.addEventListener('load', sync);
  }
})();
